import { FC, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useConfirm from 'src/hooks/useConfirm';
import useViewDepartment from 'src/hooks/useViewDepartments';
import { Department } from 'src/services/useGetDepartmentApi';

interface DeleteDepartmentProps {
  selectedRows: Department[];
}

const DeleteDepartment: FC<DeleteDepartmentProps> = ({ selectedRows }) => {
  const { confirm } = useConfirm();

  const { deleteDepartment, isDepartmentDeleting } = useViewDepartment();
  const { setDepartmentTableData, t, refreshTable } = useViewDepartment();
  const isDeleteEligibleRecords = useMemo(
    () => selectedRows.every((item) => !item?.isDeleted),
    [selectedRows]
  );

  const isDeleteDisable = useMemo(
    () => selectedRows.length === 0 || !isDeleteEligibleRecords,
    [isDeleteEligibleRecords, selectedRows.length]
  );
  const handleDeleteDepartment = useCallback(async () => {
    const isDeleteAcccepted = await confirm({
      title: t('Delete Department(s)'),
      content: (
        <div>
          {t(
            'The selected department(s) will be deleted and the associated GL Codes and Members will be unassigned.'
          )}
        </div>
      ),
      confirmButtonText: t('OK'),
      cancelButtonText: t('Cancel'),
    });

    if (isDeleteAcccepted) {
      deleteDepartment({
        departmentIds: selectedRows
          ?.filter((el) => !el?.isNewDepartmentName)
          ?.map((item) => item?.departmentId),
      }).then((res) => {
        toast.success(t('Record(s) Deleted Successfully'));
        refreshTable(false);

        const newlyAddedDepartment = selectedRows
          ?.filter((el) => el?.isNewDepartmentName)
          ?.map((item) => item.departmentId);

        const departmentIds = [...res.processedIds];

        setDepartmentTableData((prev: any) => {
          const prevItems = [...prev]?.filter(
            (el) => ![...newlyAddedDepartment, ...departmentIds]?.includes(el?.departmentId)
          );
          return prevItems;
        });
      });
    }
  }, [confirm, deleteDepartment, refreshTable, selectedRows, setDepartmentTableData, t]);

  return (
    <PopUp
      popupAlign={{ horizontal: 'center', vertical: 'top' }}
      content={
        <div className="m-1 max-w-[350px]">
          <PopUpContentWrapper
            type={'Information'}
            contents={['Please select one or more record(s) to Delete']}
          />
        </div>
      }
      show={isDeleteDisable}
    >
      <Button
        color="error"
        onClick={handleDeleteDepartment}
        disabled={isDeleteDisable}
        loading={isDepartmentDeleting}
      >
        {t('Delete')}
      </Button>
    </PopUp>
  );
};

export default DeleteDepartment;
